/**
 * 振り返り画面用の "all"（全PartNo合算）動的合算ロジック共通ヘルパー。
 *
 * 画面表示（ReportAndDraft.tsx 内の販売数量／生産数量 "all" 表示）と
 * PDF出力用コンポーネント（PdfReviewContent.tsx）の両方で利用する。
 */

type ChartData = {
  labels: any[];
  // null は「データなし（線を引かない）」を意味する。境界の前/後で datasets を切り替える設計のため、
  // 入力データには null が混じる可能性があり、合算結果も null を保持する。
  datasets: Array<{ data: (number | null)[]; [key: string]: any }>;
};

type SummaryValues = {
  actualVsPlan?: { planValue: number; actualValue: number; rate: number };
  forecastAccuracy?: { prevEstimate: number; currActual: number; rate: number };
  finalOutlook?: { planTotal: number; outlookTotal: number; rate: number };
};

/**
 * sections 配列内の chartData.datasets を datasetIndex × 要素index 単位で月別に合算する。
 * 先頭要素の chartData をベース（labels と datasets メタ情報）として、各 dataset の data 配列を全PartNoで足し合わせる。
 *
 * @param sections 合算対象のPartNo別sectionsまたは生産数量PartNo別配列
 * @param chartDataField 取得するchartDataのフィールド名（例: "chartData", "cumulativeChartData"）
 * @returns 合算後のChartData。先頭要素がない/datasetsがない場合はnull
 */
export function aggregateChartDatasets<T extends Record<string, any>>(
  sections: T[],
  chartDataField: keyof T,
): ChartData | null {
  if (!sections || sections.length === 0) return null;
  const firstSec = sections[0];
  const baseChartData = firstSec[chartDataField] as ChartData | undefined;
  if (!baseChartData?.datasets) return null;

  const labels = baseChartData.labels;
  const datasets = baseChartData.datasets.map((ds: any, dsIdx: number) => {
    const len = (ds.data ?? []).length;
    // 全 PartNo で null だった月は null のまま保持する（線を引かない）。
    // 初期値を 0 にしてしまうと、生産数量グラフの「実績(境界後null)」「今回見通し(境界前null)」のような
    // 元々 null だった月が 0 化して、申請書画面の「all」表示で 0 の線が描画されてしまう。
    const arr: (number | null)[] = new Array(len).fill(null);
    for (const sec of sections) {
      const data = (sec[chartDataField] as ChartData | undefined)?.datasets?.[dsIdx]?.data ?? [];
      for (let i = 0; i < len; i++) {
        const v = data[i];
        if (typeof v === "number" && isFinite(v)) {
          arr[i] = (arr[i] ?? 0) + v;
        }
      }
    }
    return { ...ds, data: arr };
  });

  return { labels, datasets };
}

/**
 * sections 配列内の summaryValues を合算してサマリーカード3枚分の値を再計算する。
 * actualVsPlan / forecastAccuracy / finalOutlook それぞれで「合算した分子・分母」から rate を再計算する。
 *
 * @param sections 合算対象のPartNo別sections
 * @returns 合算後の SummaryValues
 */
export function aggregateSummaryValues<T extends { summaryValues?: SummaryValues }>(
  sections: T[],
): SummaryValues {
  const sumField = (
    key: keyof SummaryValues,
    field: string,
  ): number => {
    return sections.reduce<number>((acc, sec) => {
      const sv = sec.summaryValues as Record<string, any> | undefined;
      const v = sv?.[key as string]?.[field];
      return acc + (typeof v === "number" && isFinite(v) ? v : 0);
    }, 0);
  };

  return {
    actualVsPlan: (() => {
      const planValue = sumField("actualVsPlan", "planValue");
      const actualValue = sumField("actualVsPlan", "actualValue");
      const rate = planValue !== 0 ? ((actualValue - planValue) / Math.abs(planValue)) * 100 : 0;
      return { planValue, actualValue, rate };
    })(),
    forecastAccuracy: (() => {
      const prevEstimate = sumField("forecastAccuracy", "prevEstimate");
      const currActual = sumField("forecastAccuracy", "currActual");
      const rate = prevEstimate !== 0 ? ((currActual - prevEstimate) / Math.abs(prevEstimate)) * 100 : 0;
      return { prevEstimate, currActual, rate };
    })(),
    finalOutlook: (() => {
      const planTotal = sumField("finalOutlook", "planTotal");
      const outlookTotal = sumField("finalOutlook", "outlookTotal");
      const rate = planTotal !== 0 ? ((outlookTotal - planTotal) / Math.abs(planTotal)) * 100 : 0;
      return { planTotal, outlookTotal, rate };
    })(),
  };
}
